import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, Popover, Tooltip } from "antd";
import { DownOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import moment from "moment";
import DatePicker from "../../core/components/DatePicker";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import { useApp } from "../../core/context/AppContext";
import { LocalStorageService } from "../../core/service/local-storage-service";
import { getDateFromNow } from "../../core/utils/date-utils";
import { useMediaQuery } from "../../core/utils/useMediaQuery";
import "./quick-filter-bar.css";

/*
 * Grid üstü hızlı filtre çubuğunun ortak parçaları (çekim: StudyQuickFilterBar, rapor: ReportQuickFilterBar).
 *
 * Parçaların kendi durumu yok (arama kutusunun yazılmakta olan metni hariç): basılı görünen her şey UYGULANAN
 * filtreden (queryModel.filter) hesaplanır ve her tık filtreyi hemen uygular. Sayfanın sol paneli uygulanan
 * filtreyi izleyip formunu eşitler, yani çubuk ile panel hiçbir zaman ayrışmaz.
 */

const { RangePicker } = DatePicker;

// Modaliteler id ile değil İSİMLE bağlanır: id kurulumdan kuruluma değişebilir.
// Varsayılanlar çekim grid'inin GetDataGrid loglarından (Eylül 2026, 7 gün): MR ve CT çubukta açık,
// kalanlar "Diğer" menüsünde bu kullanım sırasıyla; listede olmayan bir modalite sona, alfabetik eklenir.
export const VISIBLE_MODALITIES = ['MR', 'CT'];
export const MODALITY_ORDER = ['MR', 'CT', 'MG', 'OT', 'CR', 'US', 'DX', 'XA', 'PX', 'SR', 'AI'];

export const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

const sameDay = (a, b) => !!a && !!b && moment(a).isSame(moment(b), 'day');

const sameSet = (a, b) => a.length === b.length && a.every(x => b.includes(x));

// Beklenen değer uygulanan değerle eşleşiyor mu: diziler küme olarak, boş değerler birbirine eşit.
export const fieldMatches = (expected, actual) => {
    if (Array.isArray(expected))
        return sameSet(expected, isEmpty(actual) ? [] : actual);
    if (isEmpty(expected))
        return isEmpty(actual);
    return expected === actual;
};

const modalityName = (m) => `${m?.name ?? ''}`.trim().toUpperCase();

/**
 * Uygulanan filtre + değiştiriciler.
 * apply: değişiklikleri uygulanan filtrenin üstüne yazar; replace: filtrenin tamamını değiştirir (Sıfırla).
 * windowFilterKey: sayfadan çıkıp dönünce sol panelin filtreyi geri yüklediği global ('studyFilter' / 'reportFilter').
 * ready: sol panel mount olup ilk filtreyi uygulayana kadar false — çubuk yarım durum göstermesin.
 */
export const useAppliedFilter = (windowFilterKey) => {
    const { queryModel, onFilterPageSort } = useDataGrid();
    const filter = queryModel?.filter || {};

    const replace = (next) => {
        window[windowFilterKey] = next;
        onFilterPageSort({ filter: next });
    };

    return {
        filter,
        apply: (changes) => replace({ ...filter, ...changes }),
        replace,
        ready: Object.keys(filter).length > 0,
    };
};

/**
 * Serbest arama durumu. Arama yalnız Enter/ikonla uygulanır; yazılan metin o ana kadar yerel durumda bekler.
 * active: kutuda değer olduğu an (henüz uygulanmasa da) true — tarihler bu durumda kilitlenir, çünkü arama
 * tüm tarihlerde çalışır (sol panel de aynı davranır).
 */
export const useQuickSearch = (filter, apply) => {
    const applied = filter.searchText || '';
    const [text, setText] = useState(applied);

    // Arama sol panelden yapıldıysa (ya da "Sıfırla" ile silindiyse) kutu da uygulanan değeri göstersin.
    useEffect(() => {
        setText(applied);
    }, [applied]);

    // Boşaltıldığında alan tamamen kalksın; boş string sunucuya gitmesin.
    // antd temizleme ikonunda da onSearch('') çağırır → arama kaldırılıp tarih filtresi geri gelir.
    const submit = (value) => {
        const next = `${value ?? ''}`.trim() || undefined;
        if (next === (applied || undefined))
            return;
        apply({ searchText: next });
    };

    return { text, setText, submit, active: !!text?.trim() };
};

export function QuickSearch({ search, hint }) {
    const { t } = useTranslation();

    return (
        <Tooltip title={hint} mouseEnterDelay={0.6} placement="bottomLeft">
            <div className={`qf-search${search.active ? ' is-active' : ''}`}>
                <Input.Search
                    size="small"
                    allowClear
                    value={search.text}
                    placeholder={t('Type anything: name, ID, number...')}
                    onChange={(e) => search.setText(e.target.value)}
                    onSearch={search.submit}
                />
            </div>
        </Tooltip>
    );
}

/**
 * Tarih bloğu: hazır aralıklar + ‹ aralık › + aralık seçici. beginKey/endKey yönetilen filtre alanlarıdır
 * (sayfa, panelde hangi tarih türü seçiliyse onu verir). presets: [{ key, label, option: DATE_OPTIONS.* }].
 * allowClear: seçicinin çarpısıyla tarih filtresi tamamen kaldırılabilir (tarihsiz sorgu sayfanın kaldırabildiği yerde).
 */
export function QuickDateRange({ filter, apply, beginKey, endKey, presets, disabled, label, allowClear = false }) {
    const { t } = useTranslation();
    // Dokunmatik ekranda tarih kutusu yazılamaz: dokununca klavye açılıp takvimin üstünü kapatıyordu.
    const isTouch = useMediaQuery('(pointer: coarse)');

    const begin = filter[beginKey] ? moment(filter[beginKey]) : null;
    const end = filter[endKey] ? moment(filter[endKey]) : null;

    const applyDates = (start, finish) => apply({ [beginKey]: start, [endKey]: finish });

    const activePreset = presets.find(p => {
        const d = getDateFromNow(p.option);
        return sameDay(begin, d.start) && sameDay(end, d.end);
    })?.key;

    // Aralığı kendi uzunluğu kadar kaydırır: tek gün → ±1 gün, 6 günlük aralık → önceki/sonraki 6 gün.
    // İleri giderken bitiş bugünü geçmez.
    const shiftDates = (direction) => {
        const span = end.clone().startOf('day').diff(begin.clone().startOf('day'), 'days') + 1;
        const nextBegin = begin.clone().add(direction * span, 'days');
        const nextEnd = moment.min(end.clone().add(direction * span, 'days'), moment());
        applyDates(nextBegin, nextEnd);
    };
    const canGoNext = !!end && end.isBefore(moment(), 'day');

    return (
        <div className={`qf-group${disabled ? ' is-disabled' : ''}`} role="group" aria-label={label}>
            <div className="qf-seg">
                {presets.map(p => (
                    <button
                        key={p.key}
                        type="button"
                        disabled={disabled}
                        aria-pressed={activePreset === p.key}
                        onClick={() => {
                            const d = getDateFromNow(p.option);
                            applyDates(d.start.clone(), d.end.clone());
                        }}
                    >
                        {t(p.label)}
                    </button>
                ))}
            </div>
            <div className="qf-stepper">
                <Tooltip title={t('Previous')}>
                    <Button
                        size="small"
                        icon={<LeftOutlined />}
                        disabled={disabled || !begin || !end}
                        onClick={() => shiftDates(-1)}
                    />
                </Tooltip>
                <RangePicker
                    size="small"
                    allowClear={allowClear}
                    inputReadOnly={isTouch}
                    format="DD.MM.YY"
                    disabled={disabled}
                    locale={LocalStorageService.getAntLocale()}
                    value={[begin, end]}
                    onChange={(range) => {
                        if (range?.[0] && range?.[1])
                            applyDates(range[0], range[1]);
                        // Çarpı: antd onChange(null) çağırır → iki uç birlikte kalkar.
                        else if (!range && allowClear)
                            applyDates(null, null);
                    }}
                />
                <Tooltip title={t('Next')}>
                    <Button
                        size="small"
                        icon={<RightOutlined />}
                        disabled={disabled || !begin || !canGoNext}
                        onClick={() => shiftDates(1)}
                    />
                </Tooltip>
            </div>
        </div>
    );
}

/** Tekli seçim: options [{ value, label }], seçili olan value ile tam eşit olan. */
export function QuickSegment({ options, value, onChange, ariaLabel }) {
    return (
        <div className="qf-seg" role="group" aria-label={ariaLabel}>
            {options.map(option => (
                <button
                    key={String(option.value)}
                    type="button"
                    aria-pressed={option.value === value}
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}

/** Aç/kapa çip. */
export function QuickChip({ pressed, onClick, icon, children, className }) {
    return (
        <button
            type="button"
            className={className ? `qf-chip ${className}` : 'qf-chip'}
            aria-pressed={!!pressed}
            onClick={onClick}
        >
            {icon}
            {children}
        </button>
    );
}

/** Modalite çipleri (çoklu): VISIBLE_MODALITIES açıkta, kalanlar "Diğer" menüsünde. */
export function QuickModalityFilter({ filter, apply }) {
    const { t } = useTranslation();
    const { modalities } = useApp();

    const sortedModalities = useMemo(() => {
        const rank = (m) => {
            const i = MODALITY_ORDER.indexOf(modalityName(m));
            return i === -1 ? MODALITY_ORDER.length : i;
        };
        return [...(modalities || [])]
            .sort((a, b) => rank(a) - rank(b) || modalityName(a).localeCompare(modalityName(b)));
    }, [modalities]);

    if (sortedModalities.length === 0)
        return null;

    const selectedIds = filter.modalities || [];
    const visibleModalities = sortedModalities.filter(m => VISIBLE_MODALITIES.includes(modalityName(m)));
    const otherModalities = sortedModalities.filter(m => !VISIBLE_MODALITIES.includes(modalityName(m)));
    const selectedOthers = otherModalities.filter(m => selectedIds.includes(m.id));

    const toggleModality = (id) => apply({
        modalities: selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id],
    });

    const setOtherModalities = (ids) => apply({
        modalities: [
            ...selectedIds.filter(id => !otherModalities.some(m => m.id === id)),
            ...ids,
        ],
    });

    return (
        <div className="qf-group" role="group" aria-label={t('Modality')}>
            {visibleModalities.map(m => (
                <button
                    key={m.id}
                    type="button"
                    className="qf-chip qf-chip-code"
                    aria-pressed={selectedIds.includes(m.id)}
                    onClick={() => toggleModality(m.id)}
                >
                    {m.name}
                </button>
            ))}
            {otherModalities.length > 0 && (
                <Popover
                    trigger="click"
                    placement="bottomLeft"
                    content={(
                        <Checkbox.Group
                            className="qf-more"
                            value={selectedOthers.map(m => m.id)}
                            onChange={setOtherModalities}
                        >
                            {otherModalities.map(m => (
                                <Checkbox key={m.id} value={m.id}>{m.name}</Checkbox>
                            ))}
                        </Checkbox.Group>
                    )}
                >
                    <button
                        type="button"
                        className="qf-chip qf-chip-code qf-chip-more"
                        aria-pressed={selectedOthers.length > 0}
                    >
                        {selectedOthers.length > 0 ? selectedOthers.map(m => m.name).join(', ') : t('Other')}
                        <DownOutlined />
                    </button>
                </Popover>
            )}
        </div>
    );
}

export function QuickResetLink({ onClick }) {
    const { t } = useTranslation();

    return (
        <button type="button" className="qf-link qf-reset" onClick={onClick}>
            {t('Reset')}
        </button>
    );
}
